import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { SignOut, Package, FileText, User } from "@phosphor-icons/react";
import { api, formatPrice } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATUS_LV = { new: "Jauns", processing: "Apstrādē", shipped: "Nosūtīts", done: "Izpildīts" };

export default function AccountPage() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [tab, setTab] = useState("orders");
  const navigate = useNavigate();

  useEffect(() => {
    if (user === null) navigate("/pieteikties");
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/orders").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/quotes").then(({ data }) => setQuotes(data)).catch(() => {});
  }, [user]);

  if (!user) return <div className="py-24 text-center text-sm text-slate-500">Ielādē...</div>;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8" data-testid="account-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Mans konts</h1>
          <p className="mt-2 text-sm text-slate-600 flex items-center gap-2"><User size={16} /> {user.name} · {user.email}</p>
          {user.is_b2b && (
            <div className="mt-3 inline-flex items-center gap-2 border border-slate-300 px-3 py-1.5 text-xs font-semibold" data-testid="b2b-status-badge">
              B2B: {user.company_name}
              <span className={user.b2b_status === "approved" ? "text-[#16A34A]" : "text-[#EAB308]"}>
                {user.b2b_status === "approved" ? `Apstiprināts · atlaide ${user.discount_pct}%` : "Gaida apstiprinājumu"}
              </span>
            </div>
          )}
        </div>
        <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/"); }}
          className="btn-industrial inline-flex items-center gap-2 border border-slate-300 px-4 py-2.5 text-xs font-semibold hover:bg-[#2E86D1] hover:text-white hover:border-[#2E86D1]">
          <SignOut size={16} /> Iziet
        </button>
      </div>
      <p className="mt-4 border border-blue-200 bg-blue-50 p-3 text-sm text-slate-600" data-testid="account-demo-notice">Demo konts — pasūtījumu un piedāvājumu vēsture glabājas tikai šajā pārlūkā.</p>

      <div className="mt-8 flex border border-slate-300 w-fit">
        <button data-testid="tab-orders" onClick={() => setTab("orders")}
          className={`btn-industrial flex items-center gap-2 px-5 py-2.5 text-xs font-semibold ${tab === "orders" ? "bg-[#1D1D1F] text-white" : "hover:bg-[#F4F5F7]"}`}>
          <Package size={16} /> Pasūtījumi ({orders.length})
        </button>
        <button data-testid="tab-quotes" onClick={() => setTab("quotes")}
          className={`btn-industrial flex items-center gap-2 px-5 py-2.5 text-xs font-semibold ${tab === "quotes" ? "bg-[#1D1D1F] text-white" : "hover:bg-[#F4F5F7]"}`}>
          <FileText size={16} /> Piedāvājumi ({quotes.length})
        </button>
      </div>

      {tab === "orders" && (
        <div className="mt-6 space-y-4" data-testid="orders-list">
          {orders.length === 0 && (
            <p className="py-12 text-center text-sm text-slate-500">Vēl nav pasūtījumu. <Link to="/katalogs" className="font-bold text-[#2E86D1]">Doties uz katalogu →</Link></p>
          )}
          {orders.map((o) => (
            <div key={o.order_id} className="rounded-2xl overflow-hidden border border-border">
              <div className="flex flex-wrap items-center justify-between gap-2 bg-[#F4F5F7] px-4 py-2.5 text-sm">
                <span className="font-bold">{o.number}</span>
                <span className="text-xs text-slate-500">{new Date(o.created_at).toLocaleDateString("lv-LV")}</span>
                <span className="text-xs font-semibold">{STATUS_LV[o.status] || o.status}</span>
                <span className="font-black font-heading">{formatPrice(o.total)}</span>
              </div>
              <div className="px-4 py-3 text-xs text-slate-600 space-y-1">
                {o.items.map((i) => <p key={i.sku}>{i.qty}× {i.name} — {formatPrice(i.total)}</p>)}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "quotes" && (
        <div className="mt-6 space-y-4" data-testid="quotes-list">
          {quotes.length === 0 && (
            <p className="py-12 text-center text-sm text-slate-500">Vēl nav piedāvājumu pieprasījumu. <Link to="/piedavajums" className="font-bold text-[#2E86D1]">Pieprasīt piedāvājumu →</Link></p>
          )}
          {quotes.map((qt) => (
            <div key={qt.quote_id} className="rounded-2xl overflow-hidden border border-border">
              <div className="flex flex-wrap items-center justify-between gap-2 bg-[#F4F5F7] px-4 py-2.5 text-sm">
                <span className="font-bold">{qt.number}</span>
                <span className="text-xs text-slate-500">{new Date(qt.created_at).toLocaleDateString("lv-LV")}</span>
                <span className="text-xs font-semibold">{STATUS_LV[qt.status] || qt.status}</span>
              </div>
              <div className="px-4 py-3 text-xs text-slate-600 space-y-1">
                {qt.items?.map((i, idx) => <p key={idx}>{i.qty}× {i.name}</p>)}
                {qt.message && <p className="italic">"{qt.message}"</p>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
